type Environment = 'localDev' | 'prod';

type EnvValues = Record<string, string | undefined>;

// Production builds replace these direct process.env reads with string literals.
// Bun dev leaves them intact, so catch the browser's missing `process` global and
// continue to the runtime sources below. A `typeof process` branch cannot be used
// here because it survives the production build and discards the embedded values.
const BuildTimeEnv: EnvValues = (() => {
  try {
    return {
      BUN_PUBLIC_ENVIRONMENT: process.env.BUN_PUBLIC_ENVIRONMENT,
      BUN_PUBLIC_SERVICE_BASE_URL: process.env.BUN_PUBLIC_SERVICE_BASE_URL,
      BUN_PUBLIC_DEV_API_BASE_URL: process.env.BUN_PUBLIC_DEV_API_BASE_URL,
      BUN_PUBLIC_ENABLE_DEV_JSON_IMPORT:
        process.env.BUN_PUBLIC_ENABLE_DEV_JSON_IMPORT,
    };
  } catch (error) {
    if (error instanceof ReferenceError) {
      return {};
    }
    throw error;
  }
})();

const ImportMetaEnv = (import.meta as ImportMeta & { env?: EnvValues }).env;
const ProcessEnv =
  typeof process === 'undefined'
    ? undefined
    : (process.env as unknown as EnvValues);

export function readPublicEnv(
  key: string,
  envSources: ReadonlyArray<EnvValues | undefined> = [
    BuildTimeEnv,
    ImportMetaEnv,
    ProcessEnv,
  ],
): string | undefined {
  for (const envSource of envSources) {
    if (envSource === undefined) {
      continue;
    }

    const value = envSource[key];
    if (value !== undefined) {
      return value;
    }
  }

  return undefined;
}

const EnvironmentEnv = readPublicEnv('BUN_PUBLIC_ENVIRONMENT');
const DevAPIBaseURLEnv = readPublicEnv('BUN_PUBLIC_DEV_API_BASE_URL');
const ProdAPIBaseURLEnv = readPublicEnv('BUN_PUBLIC_SERVICE_BASE_URL');
const EnableDevJSONImportEnv = readPublicEnv(
  'BUN_PUBLIC_ENABLE_DEV_JSON_IMPORT',
);

export const EnableDevJSONImport = EnableDevJSONImportEnv === 'true';

function resolveEnvironment(rawEnvironment: string | undefined): Environment {
  switch (rawEnvironment?.toLowerCase()) {
    case 'prod':
      return 'prod';
    case 'localdev':
    case 'local_dev':
      return 'localDev';
    default:
      return 'localDev';
  }
}

export const CurrentEnvironment: Environment =
  resolveEnvironment(EnvironmentEnv);

const DevelopmentBaseURL = DevAPIBaseURLEnv ?? 'http://localhost:8080';
const ProductionBaseURL = ProdAPIBaseURLEnv ?? 'https://beam.codifyapp.ai';

export function serviceURL({ endpoint }: { endpoint: string }): string {
  const base = serviceBaseURL();
  const normalizedEndpoint = endpoint.startsWith('/')
    ? endpoint.slice(1)
    : endpoint;

  return base.endsWith('/')
    ? base + normalizedEndpoint
    : `${base}/${normalizedEndpoint}`;
}

export function serviceBaseURL(): string {
  switch (CurrentEnvironment) {
    case 'localDev':
      return DevelopmentBaseURL;
    case 'prod':
      return ProductionBaseURL;
  }
}
